import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MapPin, Globe, Mail, ArrowRight, BookOpen, ExternalLink } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/motion";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const TWELVE_STEP = [
  {
    title: "Twelve Steps and Twelve Traditions (AA)",
    desc: "In-depth exploration of the 12-step principles and practices.",
    href: "https://www.aa.org/twelve-steps",
  },
  {
    title: "12 Steps in 4 Hours Group",
    desc: "Twelve-step recovery from cocaine and all other mind-altering substances since May 27, 2007. Concise workbook guide to the 12 steps of recovery.",
    href: "https://12steps4hours.org",
  },
  {
    title: "4-Hour 12-Step Workshop",
    desc: "Workshop of the 12 steps of recovery in 4 hours.",
    href: "https://bigbooksponsorship.org",
  },
];

const REFERRALS = [
  {
    name: "Bridges Medical Clinic",
    kind: "Virtual / online clinic",
    website: "https://bridgesmedical.ca",
    address: "3-1136 Centre St #121, Thornhill, ON L4J 3M8 (mailing only)",
    phone: "1-877-505-0336",
    fax: "289-472-5657",
    email: "info@bridgesmedical.ca",
  },
  {
    name: "ConnexOntario",
    kind: "Online service · 24/7 referrals",
    website: "https://connexontario.ca",
    phone: "1-866-531-2600",
  },
  {
    name: "IDA Pharmacy",
    kind: "Community pharmacy",
    website: "https://share.google/BGalaQWmaxor3dKkG",
    address: "3750 St Clair Ave E #6, Scarborough, ON M1M 1M5",
    phone: "647-352-1113",
  },
  {
    name: "Alpha House Recovery Community",
    kind: "Recovery community · Toronto",
    website: "https://alphahousetoronto.ca",
    address: "647 Broadview Ave, Toronto, ON M4K 2N9",
    phone: "416-469-1700",
  },
  {
    name: "Madison Community Services",
    kind: "Community mental-health services",
    website: "https://madisoncs.org",
    address: "355 Church Street, Toronto, ON M5B 1Z8",
    phone: "416-977-1333",
  },
  {
    name: "Michael Garron Detox",
    kind: "Withdrawal management · East Toronto",
    address: "985 Danforth Ave, Toronto, ON M4J 1M1",
    phone: "416-461-2010",
  },
  {
    name: "St Michael's Withdrawal Management",
    kind: "Downtown Toronto",
    address: "333 Sherbourne St, Toronto, ON M5A 2S5",
    phone: "416-864-5978",
  },
  {
    name: "St. Joseph Withdrawal Management",
    kind: "Located in St. Joseph's Health Centre",
    phone: "416-530-6141",
  },
];

const Card = ({ r, i }) => (
  <Reveal delay={(i % 3) * 0.06}>
    <div className="relative h-full rounded-2xl bg-white border border-line p-6 md:p-7 hover:shadow-[0_20px_40px_-16px_rgba(46,106,138,0.28)] transition-shadow">
      <span className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-terracotta/70" aria-hidden />
      <p className="font-serif text-forest text-xl md:text-2xl font-bold tracking-tight leading-tight">{r.name}</p>
      <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-ink2">{r.kind}</p>
      <ul className="mt-4 space-y-2.5 text-[14px] text-ink2">
        {r.website && (
          <li className="flex items-start gap-2.5">
            <Globe className="w-4 h-4 mt-0.5 shrink-0 text-terracotta" />
            <a href={r.website} target="_blank" rel="noopener noreferrer" className="hover:text-terracotta underline underline-offset-2 break-all">{r.website.replace(/^https?:\/\//, "")}</a>
          </li>
        )}
        {r.address && (
          <li className="flex items-start gap-2.5">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-terracotta" />
            <span>{r.address}</span>
          </li>
        )}
        {r.phone && (
          <li className="flex items-start gap-2.5">
            <Phone className="w-4 h-4 mt-0.5 shrink-0 text-terracotta" />
            <a href={`tel:${r.phone.replace(/[^0-9+]/g, "")}`} className="hover:text-terracotta">{r.phone}</a>
          </li>
        )}
        {r.fax && (
          <li className="pl-6 text-ink2/80 text-[13px]">Fax: {r.fax}</li>
        )}
        {r.email && (
          <li className="flex items-start gap-2.5">
            <Mail className="w-4 h-4 mt-0.5 shrink-0 text-terracotta" />
            <a href={`mailto:${r.email}`} className="hover:text-terracotta">{r.email}</a>
          </li>
        )}
      </ul>
    </div>
  </Reveal>
);

const ResourcesPage = () => {
  const navigate = useNavigate();
  useSEO({
    title: "Addiction Recovery Resources — Toronto | 12-Step, Detox & Community Support",
    description:
      "A curated list of trusted 12-step programs, medical clinics, withdrawal-management services and community organisations serving people in recovery in the Greater Toronto Area.",
    keywords: "addiction resources Toronto, 12 step Toronto, withdrawal management Toronto, ConnexOntario, detox Toronto",
    canonical: "https://newbeginningsrecovery.ca/resources",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="resources-page" className="bg-sand min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 md:pt-48 pb-16 bg-forestdark text-sand relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }} aria-hidden />
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 relative">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Toronto recovery directory
            </p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              Recovery <span className="italic text-terracotta">Resources</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sand/80 text-lg leading-relaxed">
              You are not alone. Below is a curated list of trusted 12-step programs, medical clinics,
              withdrawal-management services and community organisations serving people in recovery
              across the Greater Toronto Area.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 12-step resources */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <div className="max-w-2xl mb-10">
            <p className="text-[11px] uppercase tracking-[0.28em] text-forest font-semibold">Programs of recovery</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-forest tracking-tight">12-Step recovery resources</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TWELVE_STEP.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.06}>
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`resource-12step-${i}`}
                  className="group relative block h-full rounded-2xl bg-white border border-line p-6 hover:shadow-[0_20px_40px_-16px_rgba(46,106,138,0.28)] transition-shadow"
                >
                  <span className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-terracotta/70" aria-hidden />
                  <p className="font-serif text-forest text-xl font-bold leading-tight">{r.title}</p>
                  <p className="mt-3 text-ink2 text-sm leading-relaxed">{r.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-terracotta text-sm font-semibold group-hover:gap-2 transition-all">
                    Visit resource <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Referral network */}
      <section className="py-16 md:py-20 bg-sand2">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <div className="max-w-2xl mb-10">
            <p className="text-[11px] uppercase tracking-[0.28em] text-forest font-semibold">Toronto & GTA</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-forest tracking-tight">Clinics, detox & community services</h2>
            <p className="mt-3 text-ink2">Trusted partners we regularly refer to. Please call ahead to confirm availability, hours and eligibility.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REFERRALS.map((r, i) => (
              <Card key={r.name} r={r} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-[900px] px-5 md:px-10 text-center">
          <h3 className="font-serif text-3xl md:text-4xl text-forest tracking-tight">Have a question?</h3>
          <p className="mt-3 text-ink2 max-w-xl mx-auto">Reach out to us. We are here to help — 24/7, confidential, no obligation.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="resources-call"
              className="h-12 px-6 rounded-full bg-terracotta hover:bg-[#a8664b] text-white inline-flex items-center gap-2 font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" /> Call Us Now
            </a>
            <Button
              onClick={() => navigate("/contact")}
              variant="outline"
              className="h-12 px-6 rounded-full border-forest text-forest hover:bg-forest hover:text-sand font-semibold"
            >
              Message Us <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
